
import React from 'react';
import { RefreshCw } from 'lucide-react';
import { UserProfile } from '@/types/database.types';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { UserTableRow } from './UserTableRow';
import { UserTableEmpty } from './UserTableEmpty';
import { UserTableLoading } from './UserTableLoading';

interface UserTableProps {
  profiles?: UserProfile[];
  isLoading: boolean;
  currentUser: UserProfile | null;
  isUpdatingStatus: string | null;
  onStatusUpdate: (userId: string | null) => void;
  onRefresh: () => void;
  onEditUser: (user: { id: string; firstName: string; lastName: string }) => void;
  onDeleteUser: (user: { id: string; email: string; name: string }) => void;
  onResetPassword: (user: { id: string; email: string; name: string }) => void;
}

export const UserTable: React.FC<UserTableProps> = ({
  profiles,
  isLoading,
  currentUser,
  isUpdatingStatus,
  onStatusUpdate,
  onRefresh,
  onEditUser,
  onDeleteUser,
  onResetPassword
}) => {
  // Toggle user active status
  const handleToggleUserStatus = async (userId: string, currentStatus: string = 'active') => {
    onStatusUpdate(userId);
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    
    try {
      // Call our Edge Function to update user status
      const { data, error } = await supabase.functions.invoke('invite-user', {
        body: {
          userId,
          isActive: newStatus === 'active',
          action: 'updateStatus'
        }
      });
      
      if (error || (data && data.error)) {
        const errorMessage = error?.message || (data && data.error) || 'Unknown error';
        console.error('Error updating user status:', errorMessage);
        toast.error(`Failed to update user status: ${errorMessage}`);
      } else {
        toast.success(`User ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
        onRefresh();
      }
    } catch (err) {
      console.error('Unexpected error toggling user status:', err);
      toast.error('An unexpected error occurred');
    } finally {
      onStatusUpdate(null);
    }
  };

  if (isLoading) {
    return <UserTableLoading />;
  }

  if (!profiles || profiles.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6 text-center">
        <p className="text-muted-foreground mb-2">No users found</p>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={onRefresh}
          className="mx-auto"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="hidden md:table-cell">Created At</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {profiles.length === 0 ? (
            <UserTableEmpty hasSearchQuery={true} />
          ) : (
            profiles.map((profile) => (
              <UserTableRow 
                key={profile.id}
                profile={profile}
                isCurrentUser={currentUser && profile.id === currentUser.id}
                isUpdatingStatus={isUpdatingStatus === profile.id}
                onStatusToggle={handleToggleUserStatus}
                onEditUser={onEditUser}
                onDeleteUser={onDeleteUser}
                onResetPassword={onResetPassword}
              />
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};
